import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getStripeSessionDetails } from "../../../services/apiV2";
import { TPaymentStatus } from "../../../features/pricingPlans/pricingPlans.types";
import { selectCurrentPlan } from "../../../features/pricingPlans/pricingPlans.selector";
import { useAppSelector } from "../../../store/hooks";
import { useAnalytics } from "../../../hooks/useAnalytics";

type TStatusType = TPaymentStatus | "idle" | "error" | "downgrade" | "upgrade";

export const useStripeSessionFeedback = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<TStatusType>("idle");
  const [transactionId, setTransactionId] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract session_id - it might be a direct parameter or embedded in registeredFrom
  const extractSessionId = (): string | null => {
    let sessionId = searchParams.get("session_id");

    if (!sessionId) {
      const registeredFromRaw = searchParams.get("registeredFrom");
      if (registeredFromRaw) {
        try {
          const decoded = decodeURIComponent(registeredFromRaw);
          const match = decoded.match(/session_id=([^&]+)/);
          sessionId = match ? match[1] : null;
        } catch {
          const match = registeredFromRaw.match(/session_id=([^&]+)/);
          sessionId = match ? match[1] : null;
        }
      }
    }

    return sessionId;
  };

  const sessionId = extractSessionId();
  const statusParam = searchParams.get("status") as TStatusType;
  const redirect = searchParams.get("redirect");

  const currentPlan = useAppSelector(selectCurrentPlan);
  const { trackPurchase, trackUpsellPurchase } = useAnalytics();

  // Check if this is an upsell purchase (annual-one-time-offer route)
  // const isUpsellPurchase = location.pathname.includes("/annual-one-time-offer");

  const maybeRedirect = () => {
    if (!redirect) return;

    setTimeout(() => {
      window.open(redirect, "_self");
    }, 3000);
  };

  useEffect(() => {
    const getDetails = async () => {
      if (!sessionId) return;
      setLoading(true);
      try {
        const res = await getStripeSessionDetails({ sessionId });
        if (res.status === 200) {
          setStatus(res.data.paymentStatus);
          // Use subscriptionId as transaction_id, fallback to sessionId
          setTransactionId(res.data.subscriptionId || sessionId);
        }
      } catch {
        setStatus("error");
        setTransactionId(sessionId);
      } finally {
        setLoading(false);
      }
      maybeRedirect();
    };

    // Store sessionId as transaction_id before it's deleted
    if (sessionId) {
      setTransactionId(sessionId);
    }

    if (statusParam) {
      setStatus(statusParam);
      maybeRedirect();
    } else {
      getDetails();
    }

    setSearchParams((searchParams) => {
      searchParams.delete("session_id");
      searchParams.delete("status");
      return searchParams;
    });
  }, [sessionId, setSearchParams]);

  // Handle navigation to onboarding after successful payment for new signups
  useEffect(() => {
    if ((status === "paid" || status === "upgrade") && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      const fromSignup = urlParams.get("fromSignup") === "true";
      const registeredFromRaw = urlParams.get("registeredFrom");

      if (fromSignup) {
        // Extract registeredFrom value, handling cases where Stripe appends session_id
        let registeredFrom: string | null = null;
        if (registeredFromRaw) {
          try {
            const decoded = decodeURIComponent(registeredFromRaw);
            registeredFrom = decoded.split("?")[0].split("&")[0];
          } catch {
            registeredFrom = registeredFromRaw.split("?")[0].split("&")[0];
          }
        }

        const onboardingRoute =
          registeredFrom === "extension"
            ? "/extension-signup-onboarding"
            : "/onboarding";

        setTimeout(() => {
          setSearchParams((params) => {
            params.delete("fromSignup");
            params.delete("registeredFrom");
            return params;
          });
          navigate(onboardingRoute, { replace: true });
        }, 3000);
      }
    }
  }, [status, loading, navigate, setSearchParams]);

  useEffect(() => {
    if ((status === "upgrade" || status === "paid") && currentPlan) {
      // if (isUpsellPurchase) {
      //   // Track upsell purchase for annual-one-time-offer
      //   trackUpsellPurchase({
      //     planId: currentPlan.id,
      //     planName: currentPlan.name,
      //     planDuration: currentPlan.period || undefined,
      //     planPrice: currentPlan.price,
      //     transactionId,
      //   });
      // } else {
        // Track regular purchase for other routes
        trackPurchase({
          planId: currentPlan.id,
          planName: currentPlan.name,
          planDuration: currentPlan.period || undefined,
          planPrice: currentPlan.price,
          transactionId,
        });
      }
    // }
  }, [
    status,
    currentPlan,
    transactionId,
    trackPurchase,
    // trackUpsellPurchase,
    // isUpsellPurchase,
  ]);

  return { status, loading };
};
